/* tower28-home cache refresh 2026-06-11 v3 */
(() => {
  const clampIndex = (value, max) => {
    if (value < 0) return max;
    if (value > max) return 0;
    return value;
  };

  const productUrlCache = new Map();

  const normalizeProductQuery = (value) =>
    (value || "")
      .replace(/[\u00AE\u2122]/g, "")
      .replace(/\[[^\]]*]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const resolveProductUrl = async (rawQuery) => {
    const query = normalizeProductQuery(rawQuery);
    if (!query) return "";

    if (productUrlCache.has(query)) {
      return productUrlCache.get(query);
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=hide`,
        {
          headers: {
            Accept: "application/json"
          }
        }
      );

      if (!response.ok) {
        productUrlCache.set(query, "");
        return "";
      }

      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      const normalizedQuery = query.toLowerCase();
      const matchedProduct =
        products.find((product) => normalizeProductQuery(product.title).toLowerCase() === normalizedQuery) ||
        products.find((product) => normalizeProductQuery(product.title).toLowerCase().includes(normalizedQuery)) ||
        products[0];
      const url = matchedProduct?.url || "";

      productUrlCache.set(query, url);
      return url;
    } catch {
      productUrlCache.set(query, "");
      return "";
    }
  };

  const getFallbackProductQuery = (link) => {
    const explicitQuery = link.dataset.t28ProductQuery || "";
    if (explicitQuery) return explicitQuery;

    const card = link.closest(".tower28-card");
    if (!card) return "";

    const titleLink = card.querySelector(".tower28-card__title a");
    const titleText = titleLink?.textContent || card.querySelector(".tower28-card__title")?.textContent || "";
    return titleText.trim();
  };

  const setupAnnouncements = () => {
    document.querySelectorAll("[data-t28-announcements]").forEach((root) => {
      const items = Array.from(root.querySelectorAll("[data-t28-announcement-item]"));
      const prev = root.querySelector("[data-t28-prev]");
      const next = root.querySelector("[data-t28-next]");

      if (items.length <= 1) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        return;
      }

      let index = 0;
      let timer;

      const render = () => {
        items.forEach((item, itemIndex) => {
          item.classList.toggle("is-active", itemIndex === index);
        });
      };

      const start = () => {
        window.clearInterval(timer);
        timer = window.setInterval(() => {
          index = clampIndex(index + 1, items.length - 1);
          render();
        }, 3600);
      };

      prev?.addEventListener("click", () => {
        index = clampIndex(index - 1, items.length - 1);
        render();
        start();
      });

      next?.addEventListener("click", () => {
        index = clampIndex(index + 1, items.length - 1);
        render();
        start();
      });

      render();
      start();
    });
  };

  const setupRails = () => {
    document.querySelectorAll("[data-t28-rail]").forEach((root) => {
      const viewport = root.querySelector("[data-t28-scroll]");
      const prev = root.querySelector("[data-t28-prev]");
      const next = root.querySelector("[data-t28-next]");

      if (!viewport || !prev || !next) return;

      const update = () => {
        const maxLeft = viewport.scrollWidth - viewport.clientWidth - 4;
        prev.disabled = viewport.scrollLeft <= 4;
        next.disabled = viewport.scrollLeft >= maxLeft;
      };

      const scrollByAmount = (direction) => {
        viewport.scrollBy({
          left: viewport.clientWidth * 0.82 * direction,
          behavior: "smooth"
        });
      };

      prev.addEventListener("click", () => scrollByAmount(-1));
      next.addEventListener("click", () => scrollByAmount(1));
      viewport.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      update();
    });
  };

  const setupDrawer = () => {
    const drawer = document.querySelector("[data-t28-drawer]");
    if (!drawer) return;

    const openers = document.querySelectorAll("[data-t28-drawer-open]");
    const closers = drawer.querySelectorAll("[data-t28-drawer-close]");

    const setOpen = (isOpen) => {
      drawer.classList.toggle("is-open", isOpen);
      document.documentElement.classList.toggle("t28-lock-scroll", isOpen);
      document.body.classList.toggle("t28-lock-scroll", isOpen);
    };

    openers.forEach((button) => {
      button.addEventListener("click", () => setOpen(true));
    });

    closers.forEach((button) => {
      button.addEventListener("click", () => setOpen(false));
    });

    drawer.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => setOpen(false));
    });
  };

  const setupVariantCards = () => {
    document.querySelectorAll("[data-t28-variant-card]").forEach((card) => {
      const choices = Array.from(card.querySelectorAll("[data-t28-variant-choice]"));
      if (!choices.length) return;

      const primaryImage = card.querySelector("[data-t28-primary-image]");
      const secondaryImage = card.querySelector("[data-t28-secondary-image]");
      const idInput = card.querySelector("[data-t28-variant-id-input]");
      const priceTarget = card.querySelector("[data-t28-price-target]");
      const labelTarget = card.querySelector("[data-t28-selected-label]");

      const setChoiceState = (choice, isActive) => {
        choice.setAttribute("aria-pressed", isActive ? "true" : "false");
        if (choice.classList.contains("tower28-swatch")) {
          choice.classList.toggle("tower28-swatch--active", isActive);
        }
        if (choice.classList.contains("tower28-chip")) {
          choice.classList.toggle("tower28-chip--active", isActive);
        }
      };

      const applyChoice = (choice) => {
        choices.forEach((item) => setChoiceState(item, item === choice));

        const image = choice.dataset.t28VariantImage;
        const secondary = choice.dataset.t28VariantSecondaryImage;
        const variantId = choice.dataset.t28VariantId;
        const price = choice.dataset.t28ChoicePrice;
        const label = choice.dataset.t28VariantLabel;

        if (primaryImage && image) {
          primaryImage.src = image;
          primaryImage.srcset = "";
        }

        if (secondaryImage && secondary) {
          secondaryImage.src = secondary;
          secondaryImage.srcset = "";
        }

        if (idInput && variantId) {
          idInput.value = variantId;
        }

        if (priceTarget && price) {
          priceTarget.textContent = `- ${price}`;
        }

        if (labelTarget && label) {
          labelTarget.textContent = label;
        }
      };

      choices.forEach((choice) => {
        choice.addEventListener("click", () => applyChoice(choice));
      });

      const activeChoice = choices.find((choice) => choice.getAttribute("aria-pressed") === "true") || choices[0];
      if (activeChoice) {
        applyChoice(activeChoice);
      }
    });
  };

  const hydrateFallbackProductLinks = () => {
    document.querySelectorAll("[data-t28-force-product-link]").forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const href = link.getAttribute("href") || "";
      if (href.includes("/products/")) return;

      const query = getFallbackProductQuery(link);
      if (!query) return;

      resolveProductUrl(query).then((url) => {
        if (url) {
          link.setAttribute("href", url);
        }
      });
    });
  };

  const setupForcedProductLinks = () => {
    document.querySelectorAll("[data-t28-force-product-link]").forEach((link) => {
      link.addEventListener("click", async (event) => {
        if (!(link instanceof HTMLAnchorElement)) return;
        if (event.defaultPrevented) return;
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const href = link.getAttribute("href") || "";
        const query = getFallbackProductQuery(link);

        if (href.includes("/products/")) {
          event.preventDefault();
          event.stopPropagation();
          window.location.assign(link.href);
          return;
        }

        if (!query) return;

        event.preventDefault();
        event.stopPropagation();
        const resolvedUrl = await resolveProductUrl(query);
        if (resolvedUrl) {
          link.setAttribute("href", resolvedUrl);
          window.location.assign(resolvedUrl);
        }
      });
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    setupAnnouncements();
    setupRails();
    setupDrawer();
    setupVariantCards();
    hydrateFallbackProductLinks();
    setupForcedProductLinks();
  });
})();
